import { KatalComponent, Keys } from '../../shared/base';
import strings from './strings';

const EMOJI_STRING_MAP = {
  1: 'very_unsatisfied_option',
  2: 'unsatisfied_option',
  3: 'neutral_option',
  4: 'satisfied_option',
  5: 'very_satisfied_option',
};

/**
 * @component {kat-hmd} KatalHmd HMD (Satisfaction Survey) component, behaving as a modal.
 * @guideline Do Use to collect customer opinions on experiences or services
 * @guideline Do Listen to the submit event to handle HMD submissions. Use the error attribute to communicate submission errors to the user.
 * @guideline Dont Modify question prompts or HTML inside of the component
 * @guideline Dont Listen to the cancel button for click events. This button has been pre-wired to close the component.
 * @example Sample {"visible": false, "locale":"en-US", "rating":"0", "comment":"", "error":""}
 * @example Localized {"visible": false, "locale": "ja-JP", "rating":"0", "comment":"", "error":""}
 * @example Error {"visible": false, "locale":"en-US", "error": "Failed to submit your feedback. Please try again.", "rating":"3", "comment":"Your website is average."}
 * @event submit Fired when the HMD is submitted by the user. the details object on the event will contain rating and comment values.
 * @event dismissed Fired when the HMD is Cancelled or otherwise hidden
 * @event opened Fired when the HMD Modal Dialog is opened
 * @status Production
 */
export class KatalHmd extends KatalComponent {
  static get observedAttributes() {
    return [
      'visible',
      'error',
      'comment',
      'rating',
      'locale',
      'state',
      'subject',
      'mode',
    ];
  }

  constructor() {
    super(KatalHmd.observedAttributes);
    this._boundFormEvents = [
      'change',
      'input',
      'keydown',
      'keyup',
      'click',
      'focus',
      'blur',
      'dismissed',
    ];

    this._actionableKeyCodes = [Keys.Enter, Keys.Space];
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();

    this.registerEvents();
  }

  close() {
    this.removeAttribute('visible');
    this.dispatchCustomEvent('dismissed', { visible: this.visible });
  }

  open() {
    this.setAttribute('visible', '');
    this.dispatchCustomEvent('opened', { visible: this.visible });
  }

  reset() {
    this.close();
    this.comment = '';
    this.rating = 0;
    this.subject = '';
    this.state = 'normal';
    this.error = '';
  }

  succeed() {
    this.state = 'success';
  }

  /**
   * @param {boolean} visible Determines if the HMD is visible to the user
   * @required
   */
  get visible() {
    return this.getBooleanAttribute('visible');
  }

  set visible(value) {
    this.setBooleanAttribute('visible', value);
  }

  /**
   * @param {string} error Error text to display
   */
  get error() {
    return this.getAttribute('error') || '';
  }

  set error(value) {
    this.setAttribute('error', value);
  }

  /**
   * @param {enum} state Show success message instead of form
   * @enum {value} normal Shows the HMD form
   * @enum {value} busy Shows the HMD form, covered by a loading spinner to show that submission is in progress
   * @enum {value} success Shows a thank you message.
   */
  get state() {
    return this.getAttribute('state') || '';
  }

  set state(value) {
    this.removeOrSetAttribute('state', value);
  }

  /**
   * @param {string} comment Feedback provided by the user
   */
  get comment() {
    return this.getAttribute('comment') || '';
  }

  set comment(value) {
    this.setAttribute('comment', value);
  }

  /**
   * @param {string} subject Subject provided by the user
   */
  get subject() {
    return this.getAttribute('subject') || '';
  }

  set subject(value) {
    this.setAttribute('subject', value);
  }

  /**
   * @param {number} rating Numeric HMD sentiment rating (0-5), defaults to 0 for unselected
   */
  get rating() {
    return this.getAttribute('rating') || 0;
  }

  set rating(value) {
    let parsed = parseInt(value);
    if (isNaN(parsed)) {
      console.error('Rating must be parsable to an integer');
    } else if (parsed < 0 || parsed > 5) {
      console.error('Rating out of bounds, valid range is between 0 and 5.');
    } else {
      this.setAttribute('rating', value);
    }
  }

  /**
   * @param {string} locale Locale for the prompt text
   * @required
   */
  get locale() {
    return this.getAttribute('locale') || 'en-US';
  }

  set locale(value) {
    this.setAttribute('locale', value);
  }

  /**
   * @required
   * @param {enum} mode This is the mode of the HMD - Default is modal
   * @enum {value} modal HMD will load as a modal - Default
   * @enum {value} inline HMD will load as an inline form
   */
  get mode() {
    return this.getAttribute('mode') || 'modal';
  }
  set mode(mode) {
    this.setAttribute('mode', mode);
  }

  render() {
    const visible = this.visible;
    const error = this.error;
    const rating = this.rating;
    const comment = this.comment;
    const subject = this.subject;

    let querySelectorOrCreateElement = (parent, selector, elementTag) => {
      let element = parent.querySelector(selector);

      if (!element) {
        element = document.createElement(elementTag);
        parent.appendChild(element);
      }

      return element;
    };

    let querySelectorAndRemove = selector => {
      let element = this.querySelector(selector);
      element && element.remove();
      return !!element;
    };

    let modalElement = '';

    let rebindEventsAfterRender = false;

    if (this.mode === 'modal') {
      rebindEventsAfterRender = querySelectorAndRemove('div.hmd-container-div');
      modalElement = querySelectorOrCreateElement(
        this,
        'kat-modal',
        'kat-modal'
      );
      modalElement.visible = visible;
      modalElement.classList.add('hmd-container-div');
    } else {
      rebindEventsAfterRender = querySelectorAndRemove(
        'kat-modal.hmd-container-div'
      );
      modalElement = querySelectorOrCreateElement(
        this,
        '.hmd-container-div',
        'div'
      );
      modalElement.classList.add('hmd-container-div');
    }

    let modalContent = querySelectorOrCreateElement(
      modalElement,
      'kat-modal-content',
      'kat-modal-content'
    );

    let modalHeader = querySelectorOrCreateElement(
      modalContent,
      'kat-modal-header',
      'kat-modal-header'
    );
    modalHeader.innerHTML = this.getString('modal_title');

    let successModalBody = querySelectorOrCreateElement(
      modalContent,
      'kat-modal-body#khmdsb',
      'kat-modal-body'
    );
    successModalBody.setAttribute('id', 'khmdsb');
    successModalBody.setAttribute(
      'class',
      this.state === 'success' ? '' : 'kat-hmd-hide'
    );

    let modalBody = querySelectorOrCreateElement(
      modalContent,
      'kat-modal-body#khmdfb',
      'kat-modal-body'
    );
    modalBody.setAttribute('id', 'khmdfb');
    modalBody.setAttribute(
      'class',
      this.state !== 'success' && this.state !== 'busy' ? '' : 'kat-hmd-hide'
    );

    let loadingSpinner = querySelectorOrCreateElement(
      modalContent,
      'kat-modal-body#khmdlb',
      'kat-modal-body'
    );
    loadingSpinner.setAttribute('id', 'khmdlb');
    loadingSpinner.setAttribute(
      'class',
      this.state === 'busy' ? '' : 'kat-hmd-hide'
    );
    loadingSpinner.innerHTML = '<kat-spinner></kat-spinner>';

    let sanks = querySelectorOrCreateElement(
      successModalBody,
      'p#khmdSuccess',
      'p'
    );
    sanks.setAttribute('id', 'khmdSuccess');
    sanks.innerText = this.getString('thanks');

    let alert = querySelectorOrCreateElement(
      modalBody,
      'kat-alert',
      'kat-alert'
    );
    alert.variant = 'danger';
    alert.description = error;
    if (error.length) {
      alert.removeAttribute('dismissed');
    } else {
      alert.setAttribute('dismissed', 'true');
    }

    let promptText1 = querySelectorOrCreateElement(
      modalBody,
      'kat-label#katHmdPrompt1',
      'kat-label'
    );
    promptText1.setAttribute('id', 'katHmdPrompt1');
    promptText1.text = this.getString('rating_prompt');

    let ratingList = querySelectorOrCreateElement(
      modalBody,
      '.kat-satisfaction-rating',
      'ul'
    );
    ratingList.setAttribute('class', 'kat-satisfaction-rating');

    for (let i = 1; i <= 5; i++) {
      let emoji = querySelectorOrCreateElement(
        ratingList,
        'li.rating-' + i,
        'li'
      );
      emoji.setAttribute('class', 'rating-' + i);
      let emojilink = querySelectorOrCreateElement(emoji, 'a', 'a');
      emojilink.classList.remove('selected');
      emojilink.classList.add('hmd2-emoji', 'hmd2-emoji-' + i);
      if (rating == i) {
        emojilink.classList.add('selected');
      }
      emojilink.setAttribute('tabindex', 0);
      emojilink.setAttribute('data-rating', i);
      emojilink.setAttribute('aria-label', this.getString(EMOJI_STRING_MAP[i]));
    }

    let subjectDropdown = querySelectorOrCreateElement(
      modalBody,
      'kat-dropdown',
      'kat-dropdown'
    );
    subjectDropdown.label = this.getString('subject_prompt');
    subjectDropdown.setAttribute(
      'options',
      JSON.stringify([
        {
          name: this.getString('subject_suggestion'),
          value: 'suggestion',
        },
        {
          name: this.getString('subject_technical'),
          value: 'issue',
        },
        {
          name: this.getString('subject_compliment'),
          value: 'compliment',
        },
        {
          name: this.getString('subject_other'),
          value: 'other',
        },
      ])
    );
    subjectDropdown.setAttribute(
      'placeholder',
      this.getString('subject_select')
    );
    subjectDropdown.setAttribute('value', subject);

    let textareaElement = querySelectorOrCreateElement(
      modalBody,
      'kat-textarea',
      'kat-textarea'
    );
    textareaElement.setAttribute('id', 'feedbackText');
    textareaElement.state = 'default';
    textareaElement.maxlength = 500;
    textareaElement.value = comment;
    textareaElement.label = this.getString('comment_prompt');
    textareaElement.placeholder = this.getString('textarea_placeholder');

    let underTextarea = querySelectorOrCreateElement(
      modalBody,
      '.content.kat-row',
      'div'
    );
    underTextarea.setAttribute('class', 'content kat-row kat-no-gutters');
    underTextarea.innerHTML = `
        <div class="kat-disclaimer">
            <span>${this.getString(
              'popup_prompt'
            )}<kat-tooltip trigger-icon="info-circle" size="small" label="${this.getString(
      'disclaimer'
    )}" position="top"></kat-tooltip></span>
        </div>
        <div class="kat-text-right kat-charcount">
            ${this.getCharsRemainingText(comment.length)}
        </div>`;

    let modalFooter = querySelectorOrCreateElement(
      modalContent,
      'kat-modal-footer',
      'kat-modal-footer'
    );
    modalFooter.setAttribute(
      'class',
      this.state === 'success' || this.state === 'busy' ? 'kat-hmd-hide' : ''
    );

    let cancelBtn = querySelectorOrCreateElement(
      modalFooter,
      'kat-button#katHmdCancelBtn',
      'kat-button'
    );
    cancelBtn.setAttribute('id', 'katHmdCancelBtn');
    cancelBtn.variant = 'secondary';
    cancelBtn.label = this.getString('cancel_label');

    let submitBtn = querySelectorOrCreateElement(
      modalFooter,
      'kat-button#katHmdSubmitBtn',
      'kat-button'
    );
    submitBtn.setAttribute('id', 'katHmdSubmitBtn');
    submitBtn.label = this.getString('submit_label');

    if (!(rating > 0)) {
      submitBtn.setAttribute('disabled', 'true');
    } else {
      submitBtn.removeAttribute('disabled');
    }

    if (rebindEventsAfterRender) {
      this.registerEvents();
    }
  }

  registerEvents() {
    let form = this.querySelector('.hmd-container-div');

    this._boundFormEvents.forEach(name => {
      form.addEventListener(name, e => {
        this.handleEvent(e);
      });
    });

    let submitBtn = this.querySelector('kat-button#katHmdSubmitBtn');
    submitBtn.addEventListener('click', () => {
      if (!submitBtn.getAttribute('disabled')) {
        this.dispatchCustomEvent(
          'submit',
          {
            rating: this.rating,
            comment: this.comment,
            subject: this.subject,
          },
          false
        );
      }
    });

    let cancelBtn = this.querySelector('kat-button#katHmdCancelBtn');
    cancelBtn.addEventListener('click', () => {
      this.reset();
      this.close();
    });
  }

  isKeycodeActionable(keyCode) {
    return this._actionableKeyCodes.includes(keyCode);
  }

  handleEvent(event) {
    if (event.target.classList.contains('hmd2-emoji')) {
      if (
        event.type === 'click' ||
        (event.type === 'keyup' && this.isKeycodeActionable(event.keyCode))
      ) {
        this.rating = event.target.getAttribute('data-rating');
      }
    } else if (event.target.tagName === 'KAT-DROPDOWN') {
      if (event.type === 'change') {
        this.subject = event.target.value;
      }
    } else if (event.target.tagName === 'KAT-TEXTAREA') {
      if (event.type === 'change') {
        let inputComment = event.detail.value;
        let currentComment = this.comment;
        if (inputComment !== currentComment) {
          this.comment = inputComment;
        }
      }
      if (event.type === 'input') {
        this.querySelector(
          '.kat-charcount'
        ).innerHTML = this.getCharsRemainingText(event.detail.value.length);
      }
    } else if (event.target.tagName === 'KAT-MODAL') {
      if (event.type === 'dismissed') {
        this.visible = false;
      }
    }
  }

  getCharsRemainingText(commentLength) {
    let text = ' ' + this.getString('chars_remaining');
    return 500 - commentLength + text;
  }

  getString(stringId) {
    let locale = this.locale;
    let string = strings[stringId][locale];
    if (!string) {
      string = strings[stringId]['en-US'];
    }
    return string;
  }

  // Respond to attribute changes.
  attributeChangedCallback(attr) {
    this.render();
    if (attr !== 'mode') {
      this.dispatchCustomEvent('changed', { value: this.value }, false);
    }
  }
}

KatalComponent.register('kat-hmd', KatalHmd);
